import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import ContrastIcon from '@mui/icons-material/Contrast';
import AppsIcon from '@mui/icons-material/Apps';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useGlobalState } from '../../global/context/global-context';
import { useAccessibility } from '../../global/context/accessibility-context';
import AppMenu from './app-menu';
import SearchBar, { SearchPanel, SearchToggleButton } from './search-bar';
import { useSystemLinks } from './system-links';
import { getEntityLogoSrc, getEntityPowerLabel, getFormattedEntityName } from '../../../utils/entity-formatter';
import { useNavigate } from '../../../hooks/useNavigate';
import { useParams } from 'react-router-dom';

// Links da barra utilitária: texto claro sobre brand.deep
const utilityLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  minHeight: 40,
  px: 1,
  borderRadius: 1,
  color: 'common.white',
  fontSize: '0.875rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  transition: 'background-color 200ms',
  '&:hover': {
    backgroundColor: (theme: any) => alpha(theme.palette.common.white, 0.12),
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.125rem',
    opacity: 0.85,
  },
} as const;

const utilityIconButtonSx = {
  color: 'common.white',
  width: { xs: 44, md: 36 },
  height: { xs: 44, md: 36 },
  '&:hover': {
    backgroundColor: (theme: any) => alpha(theme.palette.common.white, 0.12),
  },
} as const;

function UtilityBar() {
  const { increaseFontSize, decreaseFontSize, toggleHighContrast, highContrast } = useAccessibility();
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const systemLinks = useSystemLinks();
  const [systemsAnchor, setSystemsAnchor] = useState<HTMLElement | null>(null);

  const getHref = (path: string) => {
    if (params.state && params.city) {
      return `/${params.state}/${params.city}${path}`;
    }
    return path;
  };

  return (
    <Box sx={{ backgroundColor: 'brand.deep', color: 'common.white' }}>
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: { xs: 48, md: 40 },
        }}
      >
        {/* Acessibilidade */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
          <Box
            component="a"
            href={getHref('/acessibilidade')}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              navigate('/acessibilidade');
            }}
            sx={utilityLinkSx}
          >
            <AccessibilityNewIcon />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Acessibilidade
            </Box>
          </Box>

          <Box
            component="a"
            href={getHref('/mapa-do-site')}
            aria-label="Mapa do site"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              navigate('/mapa-do-site');
            }}
            sx={utilityLinkSx}
          >
            <AccountTreeIcon />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Mapa do site
            </Box>
          </Box>

          <Box
            role="group"
            aria-label="Ajustes de leitura"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              pl: { xs: 0.5, md: 1 },
              borderLeft: 1,
              borderColor: (theme) => alpha(theme.palette.common.white, 0.25),
            }}
          >
            <Tooltip title="Diminuir fonte" arrow>
              <IconButton onClick={decreaseFontSize} sx={utilityIconButtonSx} aria-label="diminuir tamanho da fonte">
                <TextDecreaseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aumentar fonte" arrow>
              <IconButton onClick={increaseFontSize} sx={utilityIconButtonSx} aria-label="aumentar tamanho da fonte">
                <TextIncreaseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={highContrast ? 'Desativar alto contraste' : 'Ativar alto contraste'} arrow>
              <IconButton
                onClick={toggleHighContrast}
                aria-label="alternar alto contraste"
                aria-pressed={highContrast}
                sx={{
                  ...utilityIconButtonSx,
                  backgroundColor: (theme) => (highContrast ? alpha(theme.palette.common.white, 0.2) : 'transparent'),
                }}
              >
                <ContrastIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Sistemas: lado a lado no desktop, agrupados em menu abaixo de 900px */}
        <Box component="nav" aria-label="Serviços e sistemas" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          {systemLinks.map((link) => {
            const anchor = (
              <Box
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={utilityLinkSx}
              >
                {link.icon}
                {link.label}
              </Box>
            );
            return link.description ? (
              <Tooltip key={link.label} title={link.description} arrow>
                {anchor}
              </Tooltip>
            ) : anchor;
          })}
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Button
            onClick={(e) => setSystemsAnchor(e.currentTarget)}
            startIcon={<AppsIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            aria-haspopup="true"
            aria-expanded={Boolean(systemsAnchor)}
            aria-controls={systemsAnchor ? 'menu-sistemas' : undefined}
            sx={{ ...utilityLinkSx, minHeight: 44, px: 1.5, fontWeight: 600 }}
          >
            Sistemas
          </Button>
          <Menu
            id="menu-sistemas"
            anchorEl={systemsAnchor}
            open={Boolean(systemsAnchor)}
            onClose={() => setSystemsAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 0.5, minWidth: 260 } }}
          >
            {systemLinks.map((link) => (
              <MenuItem
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSystemsAnchor(null)}
                sx={{ minHeight: 48 }}
              >
                <ListItemIcon sx={{ color: 'brand.text' }}>{link.icon}</ListItemIcon>
                <ListItemText primary={link.label} secondary={link.description} />
                <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary', ml: 1 }} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Container>
    </Box>
  );
}

/** Logo + nome da entidade + subtítulo do poder; leva à página inicial. */
function EntityBrand() {
  const { applicationData } = useGlobalState();
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const entity = applicationData?.entity;
  const logo = getEntityLogoSrc(entity);
  const homeHref = params.state && params.city ? `/${params.state}/${params.city}/` : '/';

  return (
    <Box
      component="a"
      href={homeHref}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/');
      }}
      aria-label={`${getFormattedEntityName(entity)} — página inicial`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, md: 2 },
        minWidth: 0,
        color: 'text.primary',
        textDecoration: 'none',
        borderRadius: 1,
      }}
    >
      {logo && (
        <Box
          component="img"
          src={logo}
          alt=""
          sx={{
            height: { xs: 44, md: 64 },
            width: 'auto',
            maxWidth: { xs: 64, md: 120 },
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="p"
          sx={{
            fontFamily: (theme) => theme.typography.h1.fontFamily,
            fontWeight: 600,
            fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.375rem' },
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {getFormattedEntityName(entity)}
        </Typography>
        <Typography variant="overline" component="p" sx={{ color: 'brand.text', lineHeight: 1.6, mt: 0.25 }}>
          {getEntityPowerLabel(entity)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AppHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <React.Fragment>
      <UtilityBar />

      {/* Cabeçalho: no mobile é a faixa fixa (menu + marca + busca); no desktop rola normalmente */}
      <Box
        component="header"
        sx={{
          position: { xs: 'sticky', md: 'static' },
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: 'background.paper',
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 4 },
              minHeight: { xs: 64, md: 96 },
              py: { xs: 1, md: 2 },
            }}
          >
            <IconButton
              aria-label="abrir menu"
              aria-expanded={mobileMenuOpen}
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, width: 44, height: 44, ml: -1, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              <EntityBrand />
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' }, width: { md: 320, lg: 380 }, flexShrink: 0 }}>
              <SearchBar />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' }, mr: -1 }}>
              <SearchToggleButton open={mobileSearchOpen} onToggle={() => setMobileSearchOpen(!mobileSearchOpen)} />
            </Box>
          </Box>
          <Box sx={{ display: { md: 'none' } }}>
            <SearchPanel open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)} />
          </Box>
        </Container>
      </Box>

      {/* Navegação principal - sempre visível ao rolar (desktop) */}
      <Box
        component="nav"
        aria-label="Navegação principal"
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
          <AppMenu isMobile={false} onClose={() => { }} />
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <AppMenu isMobile={true} open={mobileMenuOpen} onClose={handleDrawerToggle} />
    </React.Fragment>
  );
}
